import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import type { ExternalObjectConf } from '@bageltechnologies/bagel-common';
import { IntegrationObjectService } from '../api/integration-object-service';
import type { AdminIntegrationByKey } from '../types/admin-integration-by-key';

export const adminIntegrationByKeyQueryKeys = {
  all: ['admin-integration-by-key'] as const,
  byKey: (accountId: string, integrationKey: string) =>
    ['admin-integration-by-key', accountId, integrationKey] as const,
};

function is404(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

export function useAdminIntegrationByKey(
  accountId: string,
  integrationKey: string,
  enabled = true,
): UseQueryResult<AdminIntegrationByKey> {
  return useQuery({
    queryKey: adminIntegrationByKeyQueryKeys.byKey(accountId, integrationKey),
    queryFn: () => IntegrationObjectService.getIntegrationByKey(accountId, integrationKey),
    enabled: Boolean(accountId && integrationKey && enabled),
    // 404 (integration key unknown in the catalog) is terminal — don't retry it.
    retry: (failureCount, error) => !is404(error) && failureCount < 3,
  });
}

function matchesObject(obj: ExternalObjectConf, originObjectKey: string): boolean {
  return obj.originObjectKey === originObjectKey || obj.originObject === originObjectKey;
}

export interface DrawerIntegrationObjectArgs {
  integrationKey: string;
  originObjectKey: string;
  enabled?: boolean;
}

export interface DrawerIntegrationObjectResult {
  /** The per-object catalog entry carrying the default `.fields` (available first, connected fallback). */
  defaultObject: ExternalObjectConf | null;
  source: 'catalog' | 'fallback';
  isLoading: boolean;
}

/**
 * Resolves the drawer's default-mapping object for one origin object from
 * `GET /admin/integration-by-key` (integration-level → one fetch, cached across all objects).
 * `accountId` comes from the route (mirrors `useIntegrationFields`). Returns `defaultObject: null` /
 * `source: 'fallback'` while loading or on 404/error, so the drawer degrades to its list fallback.
 */
export function useDrawerIntegrationObject({
  integrationKey,
  originObjectKey,
  enabled = true,
}: DrawerIntegrationObjectArgs): DrawerIntegrationObjectResult {
  const { accountId = '' } = useParams<{ accountId: string }>();
  const { data, isLoading } = useAdminIntegrationByKey(
    accountId,
    integrationKey,
    enabled && Boolean(originObjectKey),
  );

  const defaultObject =
    data && originObjectKey
      ? (data.available?.externalObjects?.find((o) => matchesObject(o, originObjectKey)) ??
        data.connected?.externalObjects?.find((o) => matchesObject(o, originObjectKey)) ??
        null)
      : null;

  return { defaultObject, source: defaultObject ? 'catalog' : 'fallback', isLoading };
}
